/* Avenora: play a product's video on product-card hover.
 *
 * Horizon renders every card media as a still poster (card-gallery.liquid calls
 * product-media with preview_image_only: true) and its "second image on hover"
 * setting just swaps to the next poster. This script pairs with the
 * <div class="avenora-card-hover-video-wrap"><video class="avenora-card-hover-video">
 * overlay that card-gallery.liquid renders for products that carry a video media.
 *
 * Behaviour:
 *  - Cards near the viewport prefetch their video, so the first hover plays from
 *    buffer instead of waiting on the network.
 *  - pointerenter shows the overlay immediately (its poster is the video's first
 *    frame, letterboxed the same way the video is) and starts playback.
 *  - pointerleave hides it, pauses and rewinds.
 *  - Clicking the card's slideshow arrows hands the card back to the stills until
 *    the pointer leaves, so shoppers can still browse photos on the card.
 *
 * Touch devices have no hover, so nothing changes there. Honors prefers-reduced-motion.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const ACTIVE = 'avenora-hover-video-active';
  const READY = 'is-ready';
  const ARROWS = 'slideshow-arrows, .slideshow-control';

  function prime(video) {
    if (video.dataset.avenoraPrimed) return;
    video.dataset.avenoraPrimed = '1';
    video.preload = 'auto';
    video.load();
  }

  const prefetch = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          prime(entry.target);
          prefetch.unobserve(entry.target);
        }
      }, { rootMargin: '600px 0px' })
    : null;

  function bind(video) {
    if (video.dataset.avenoraBound) return;
    video.dataset.avenoraBound = '1';
    const gallery = video.closest('.card-gallery');
    if (!gallery) return;

    let browsing = false;
    video.addEventListener('canplay', () => video.classList.add(READY), { once: true });
    if (prefetch) prefetch.observe(video); else prime(video);

    gallery.addEventListener('pointerenter', () => {
      if (browsing) return;
      prime(video);
      gallery.classList.add(ACTIVE);
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    });

    gallery.addEventListener('pointerleave', () => {
      browsing = false;
      gallery.classList.remove(ACTIVE);
      video.pause();
      try { video.currentTime = 0; } catch (_) { /* not seekable yet */ }
    });

    // Arrow click: let the shopper browse the stills; the video comes back on the next hover.
    gallery.addEventListener('click', (event) => {
      const target = event.target;
      if (!target || !target.closest || !target.closest(ARROWS)) return;
      browsing = true;
      gallery.classList.remove(ACTIVE);
      video.pause();
    }, true);
  }

  const scan = (root = document) => root.querySelectorAll('video.avenora-card-hover-video').forEach(bind);
  scan();
  // Cards arrive later via section rendering / filtering / pagination.
  new MutationObserver((records) => {
    for (const r of records) for (const n of r.addedNodes) if (n.nodeType === 1) scan(n);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
